import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Pencil, Trash2, Plus, ExternalLink, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../../lib/auth';

// Client-side image compression: resize to max 1200px long edge, encode as JPEG q=0.85.
// Keeps the base64 string small enough to store in MongoDB without ballooning the vendors doc.
const compressImageToDataURL = (file, maxEdge = 1200, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      img.onerror = reject;
      img.onload = () => {
        const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const SELECT_NONE = '__none__';

const buildDefaultForm = (fields) => {
  const defaults = {};
  fields.forEach((f) => {
    if (f.type === 'checkbox') defaults[f.name] = f.default ?? false;
    else if (f.type === 'number') defaults[f.name] = f.default ?? 0;
    else defaults[f.name] = '';
  });
  return defaults;
};

const ContentManager = ({ title, endpoint, fields, onUnauthorized, filter, defaults }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(buildDefaultForm(fields));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      // GET is public for content endpoints — plain fetch is fine
      const res = await fetch(`${API_URL}/api/${endpoint}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setItems(filter ? list.filter(filter) : list);
    } catch (e) {
      toast.error('Failed to load items');
    }
    setLoading(false);
  }, [API_URL, endpoint, filter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...buildDefaultForm(fields), ...(defaults || {}) });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    const populated = buildDefaultForm(fields);
    fields.forEach((f) => {
      if (item[f.name] !== undefined && item[f.name] !== null) populated[f.name] = item[f.name];
    });
    setForm(populated);
    setOpen(true);
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required
    for (const f of fields) {
      if (f.required && !form[f.name]) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    // Build payload, convert empty strings to null where appropriate
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (f.type === 'number') v = Number(v) || 0;
      else if (f.type === 'checkbox') v = !!v;
      else if (v === SELECT_NONE) v = null;
      else if (typeof v === 'string' && v.trim() === '') v = null;
      payload[f.name] = v;
    });

    try {
      const url = editingId ? `/api/${endpoint}/${editingId}` : `/api/${endpoint}`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        toast.error('Session expired — please sign in again');
        onUnauthorized?.();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || `Failed to save`);
        return;
      }
      toast.success(editingId ? 'Updated successfully' : 'Created successfully');
      setOpen(false);
      fetchItems();
    } catch (err) {
      toast.error('Network error');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/api/${endpoint}/${id}`, { method: 'DELETE' });
      if (res.status === 401) {
        toast.error('Session expired — please sign in again');
        onUnauthorized?.();
        return;
      }
      if (!res.ok) {
        toast.error('Failed to delete');
        return;
      }
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Network error');
    }
  };

  const renderContentList = () => {
    if (loading) return <p className="text-slate-600">Loading...</p>;
    if (items.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-slate-600">No items yet. Click "Add New" to create one.</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} className="border-slate-200 hover:border-cyan-300 transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                    <span>{item.name}</span>
                    {item.section && <Badge variant="outline">{item.section}</Badge>}
                    {item.category && <Badge variant="outline">{item.category}</Badge>}
                    {item.role && <Badge variant="outline" className="bg-cyan-50 text-cyan-700">{item.role}</Badge>}
                    {item.coming_soon && <Badge className="bg-amber-100 text-amber-800">Coming Soon</Badge>}
                  </CardTitle>
                  {item.subtitle && <p className="text-sm text-slate-500 mt-1">{item.subtitle}</p>}
                  {item.description && (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                    {item.website && (
                      <a href={item.website} target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-cyan-700">
                        <ExternalLink className="h-3 w-3 mr-1" />Website
                      </a>
                    )}
                    <span>Order: {item.order ?? 0}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(item)} data-testid={`edit-${endpoint}-${item.id}`}>
                    <Pencil className="h-3 w-3 mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(item.id, item.name)} data-testid={`delete-${endpoint}-${item.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" />Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        <Button
          onClick={openNew}
          className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
          data-testid={`add-${endpoint}-btn`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New
        </Button>
      </div>

      {renderContentList()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? `Edit ${title.replace(/s$/, '')}` : `Add New ${title.replace(/s$/, '')}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name} className="space-y-1">
                <Label htmlFor={f.name}>
                  {f.label}
                  {f.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {f.type === 'textarea' && (
                  <Textarea
                    id={f.name}
                    value={form[f.name] ?? ''}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                    rows={4}
                    data-testid={`field-${f.name}`}
                  />
                )}
                {f.type === 'text' && (
                  <Input
                    id={f.name}
                    value={form[f.name] ?? ''}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                    data-testid={`field-${f.name}`}
                  />
                )}
                {f.type === 'number' && (
                  <Input
                    id={f.name}
                    type="number"
                    value={form[f.name] ?? 0}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                    data-testid={`field-${f.name}`}
                  />
                )}
                {f.type === 'select' && (
                  <Select value={form[f.name] || ''} onValueChange={(v) => handleChange(f.name, v)}>
                    <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((opt) => (
                        <SelectItem key={opt || SELECT_NONE} value={opt || SELECT_NONE}>
                          {opt || '— None —'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {f.type === 'checkbox' && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={f.name}
                      checked={!!form[f.name]}
                      onCheckedChange={(v) => handleChange(f.name, v)}
                      data-testid={`field-${f.name}`}
                    />
                    <span className="text-sm text-slate-600">Yes</span>
                  </div>
                )}
                {f.type === 'image' && (
                  <div className="space-y-2">
                    {form[f.name] && (
                      <div className="relative inline-block">
                        <img
                          src={form[f.name]}
                          alt="Preview"
                          className="max-h-40 rounded border border-slate-200"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => handleChange(f.name, '')}
                          className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1 shadow hover:bg-red-50"
                          data-testid={`clear-${f.name}`}
                          aria-label="Clear image"
                        >
                          <X className="h-3 w-3 text-red-600" />
                        </button>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <label
                        className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded cursor-pointer hover:bg-slate-50 text-sm w-fit"
                        data-testid={`upload-${f.name}-label`}
                      >
                        <Upload className="h-4 w-4" />
                        <span>Upload from computer</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid={`upload-${f.name}`}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            if (file.size > 8 * 1024 * 1024) {
                              toast.error('Image too large', { description: 'Please choose a file under 8 MB.' });
                              return;
                            }
                            try {
                              const dataUrl = await compressImageToDataURL(file);
                              handleChange(f.name, dataUrl);
                              toast.success('Image ready', { description: 'Auto-resized for fast loading. Click Save to persist.' });
                            } catch {
                              toast.error('Could not process image', { description: 'Please try a different file.' });
                            }
                          }}
                        />
                      </label>
                      <div className="text-xs text-slate-500">or paste a URL below</div>
                      <Input
                        id={f.name}
                        value={typeof form[f.name] === 'string' && form[f.name].startsWith('data:') ? '' : (form[f.name] ?? '')}
                        onChange={(e) => handleChange(f.name, e.target.value)}
                        placeholder="https://…"
                        data-testid={`field-${f.name}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                data-testid="save-btn"
              >
                {editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
